import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    HStack,
    Stack,
    Text,
    useMultiStyleConfig,
    VStack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import StandardDrawer from '../standard-drawer'

export {useControlledDisclosure} from '../../hooks/use-controlled-disclosure'

export const InfoCard = ({
    id,
    title,
    drawerTitle,
    displayTitle = true,
    summary,
    children,
    editLabel,
    editAriaLabel,
    isEditable = true,
    onEdit,
    isOpen,
    setOpen,
    open,
    close,
    cardVariant = 'default',
    badge,
    badgeText,
    isDeletable = false,
    onDelete,
    deleteLabel,
    deleteAriaLabel,
    vStackSpacing = 4,
    hStackAlign = 'flex-start',
    ...rest
}) => {
    const {formatMessage} = useIntl()

    const styles = useMultiStyleConfig('InfoCard', {variant: cardVariant})

    const headingId = `info-card-heading-${id}`
    const drawerHeadingId = `info-card-drawer-heading-${id}`

    const badgeFromText =
        badgeText != null && badgeText !== '' ? (
            <Text as="span" variant="infoCardBadge">
                {badgeText}
            </Text>
        ) : null
    const resolvedBadge = badge ?? badgeFromText

    const showHeading = Boolean(displayTitle && title)
    const hasHeaderLeft = Boolean(resolvedBadge || showHeading)

    const defaultEditAria = formatMessage({
        defaultMessage: 'Edit',
        id: 'toggle_card.action.edit'
    })
    const defaultDeleteAria = formatMessage({
        defaultMessage: 'Remove',
        id: 'info_card.action.remove.assistive'
    })

    const editButton = isEditable ? (
        <Button
            variant="link"
            fontSize={{base: 'xs', lg: 'sm'}}
            onClick={() => { onEdit?.(); open();}}
            height="auto"
            aria-label={editAriaLabel || defaultEditAria}
            data-testid={`info-card-edit-button-${id}`}
        >
            {editLabel || <FormattedMessage defaultMessage="Edit" id="toggle_card.action.edit" />}
        </Button>
    ) : null

    const deleteButton =
        isDeletable && typeof onDelete === 'function' ? (
            <Button
                variant="link"
                fontSize={{base: 'xs', lg: 'sm'}}
                onClick={onDelete}
                height="auto"
                aria-label={deleteAriaLabel || defaultDeleteAria}
                data-testid={`info-card-delete-button-${id}`}
            >
                {deleteLabel || (
                    <FormattedMessage defaultMessage="Remove" id="info_card.action.remove" />
                )}
            </Button>
        ) : null

    const showActions = Boolean(editButton || deleteButton)
    const summaryBesideActions = showActions && !hasHeaderLeft

    const actionsCluster = showActions ? (
        <HStack spacing={6} flexShrink={0}>
            {editButton}
            {deleteButton}
        </HStack>
    ) : null

    const headerLeft = hasHeaderLeft ? (
        <VStack align="flex-start" spacing={4}>
            {resolvedBadge ? <Flex>{resolvedBadge}</Flex> : null}
            {showHeading ? (
                <Text as="h3" id={headingId} variant="infoCardTitle">
                    {title}
                </Text>
            ) : null}
        </VStack>
    ) : null

    const titleActionsRow =
        hasHeaderLeft || showActions ? (
            <HStack align="flex-start" justify="space-between" spacing={2} w="full">
                <Box flex="1" minW={0}>
                    {headerLeft}
                </Box>
                {actionsCluster}
            </HStack>
        ) : null

    return (
        <>
            <Box
                as="section"
                aria-labelledby={showHeading ? headingId : undefined}
                sx={styles.root}
                data-testid={`info-card-${id}`}
                {...rest}
            >
                {summaryBesideActions ? (
                    <HStack align={hStackAlign} justify="space-between" spacing={2} w="full">
                        <Box flex="1" minW={0}>
                            {summary}
                        </Box>
                        {actionsCluster}
                    </HStack>
                ) : (
                    <VStack spacing={vStackSpacing} align="stretch" w="full">
                        {titleActionsRow}
                        <Box>{summary}</Box>
                    </VStack>
                )}
            </Box>

            {isEditable ? (
                <StandardDrawer
                    data-testid={`info-card-drawer-${id}`}
                    isOpen={isOpen}
                    onClose={close}
                    ariaLabelledBy={drawerHeadingId}
                    headerTitle={drawerTitle || title}
                    headerTitleId={drawerHeadingId}
                >
                    <Stack spacing={6} h="100%">{children}</Stack>
                </StandardDrawer>
            ) : null}
        </>
    )
}

const infoCardPropTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.node.isRequired,
    drawerTitle: PropTypes.node,
    displayTitle: PropTypes.bool,
    summary: PropTypes.node.isRequired,
    children: PropTypes.node,
    editLabel: PropTypes.node,
    editAriaLabel: PropTypes.string,
    isEditable: PropTypes.bool,
    isOpen: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    open: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    cardVariant: PropTypes.string,
    badge: PropTypes.node,
    badgeText: PropTypes.node,
    isDeletable: PropTypes.bool,
    onDelete: PropTypes.func,
    deleteLabel: PropTypes.node,
    deleteAriaLabel: PropTypes.string,
    vStackSpacing: PropTypes.number,
    hStackAlign: PropTypes.string,
}

InfoCard.displayName = 'InfoCard'
InfoCard.propTypes = infoCardPropTypes
